import React, { useState, useRef, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { qrAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { QrCode, DownloadSimple, Copy, Eye, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const QR_LOGO = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pt3thi6h_D%24logo%20white.jpg";

export default function QRCodeCard({ type, targetId, title, subtitle, showStats = false }) {
  const [showModal, setShowModal] = useState(false);
  const [scanCount, setScanCount] = useState(null);
  const svgRef = useRef(null);
  
  // Use the user-facing site origin so scans land on divispend.com (or whatever
  // domain the user is currently on), not the internal backend host.
  const siteOrigin = (typeof window !== 'undefined' && window.location.origin) || process.env.REACT_APP_BACKEND_URL;
  // QR links to a scan-tracking URL that redirects
  const targetUrl = type === 'profile'
    ? `${siteOrigin}/store/${targetId}`
    : `${siteOrigin}/product/${targetId}`;
  const scanUrl = `${siteOrigin}/api/qr/scan/${type}/${targetId}`;

  const loadStats = useCallback(async () => {
    try {
      const res = await qrAPI.getStats(type, targetId);
      setScanCount(res.data.scan_count);
    } catch (err) { console.error('Failed to load QR scan stats:', err); }
  }, [type, targetId]);

  useEffect(() => {
    if (showStats) loadStats();
  }, [showStats, loadStats]);

  const downloadQR = () => {
    const svg = document.getElementById(`qr-svg-${type}-${targetId}`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#111111';
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      const link = document.createElement('a');
      link.download = `divispend-${type}-${targetId}-qr.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
    toast.success('QR code downloaded!');
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(targetUrl).then(() => {
      toast.success('Link copied!');
    });
  };

  return (
    <>
      {/* Inline Mini QR */}
      <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid={`qr-card-${type}-${targetId}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <button onClick={() => setShowModal(true)} className="flex-shrink-0 bg-white rounded-sm p-2 hover:opacity-80 transition-opacity cursor-pointer" data-testid={`qr-preview-${type}-${targetId}`}>
              <QRCodeSVG
                value={targetUrl}
                size={72}
                bgColor="#FFFFFF"
                fgColor="#111111"
                level="H"
                imageSettings={{
                  src: QR_LOGO,
                  height: 32,
                  width: 32,
                  excavate: true,
                }}
              />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <QrCode className="w-4 h-4 text-[#22C55E]" weight="duotone" />
                <h4 className="text-sm font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>{title}</h4>
              </div>
              {subtitle && <p className="text-xs text-[#999999] mb-2 truncate">{subtitle}</p>}
              {showStats && scanCount !== null && (
                <div className="flex items-center gap-1 text-xs text-[#22C55E]" data-testid={`scan-count-${type}-${targetId}`}>
                  <Eye className="w-3 h-3" />
                  <span>{scanCount} scan{scanCount !== 1 ? 's' : ''}</span>
                </div>
              )}
              <div className="flex gap-2 mt-2">
                <Button variant="ghost" size="sm" onClick={downloadQR} className="h-7 px-2 text-xs text-[#999999] hover:text-white hover:bg-[#333333]" data-testid={`download-qr-${type}-${targetId}`}>
                  <DownloadSimple className="w-3 h-3 mr-1" /> Save
                </Button>
                <Button variant="ghost" size="sm" onClick={copyUrl} className="h-7 px-2 text-xs text-[#999999] hover:text-white hover:bg-[#333333]" data-testid={`copy-link-${type}-${targetId}`}>
                  <Copy className="w-3 h-3 mr-1" /> Link
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(true)} className="h-7 px-2 text-xs text-[#22C55E] hover:bg-[#22C55E]/10" data-testid={`expand-qr-${type}-${targetId}`}>
                  Expand
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fullscreen Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setShowModal(false)} data-testid={`qr-modal-${type}-${targetId}`}>
          <div className="bg-[#1A1A1A] border border-[#333333] rounded-sm p-8 max-w-sm w-full relative" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-[#999999] hover:text-white" data-testid="close-qr-modal"><X className="w-5 h-5" /></button>
            
            <div className="text-center mb-6">
              <QrCode className="w-8 h-8 text-[#22C55E] mx-auto mb-2" weight="duotone" />
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>{title}</h3>
              {subtitle && <p className="text-sm text-[#999999] mt-1">{subtitle}</p>}
            </div>

            <div className="bg-white rounded-sm p-6 mx-auto flex items-center justify-center mb-6">
              <QRCodeSVG
                id={`qr-svg-${type}-${targetId}`}
                value={targetUrl}
                size={220}
                bgColor="#FFFFFF"
                fgColor="#111111"
                level="H"
                imageSettings={{
                  src: QR_LOGO,
                  height: 72,
                  width: 72,
                  excavate: true,
                }}
              />
            </div>

            {showStats && scanCount !== null && (
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 bg-[#22C55E]/10 px-4 py-2 rounded-full">
                  <Eye className="w-4 h-4 text-[#22C55E]" />
                  <span className="text-sm font-semibold text-[#22C55E]">{scanCount} total scans</span>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button onClick={downloadQR} className="flex-1 bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid={`modal-download-qr-${type}-${targetId}`}>
                <DownloadSimple className="w-4 h-4 mr-2" /> Download PNG
              </Button>
              <Button variant="outline" onClick={copyUrl} className="flex-1 rounded-sm border-[#333333] text-white hover:bg-[#333333]" data-testid={`modal-copy-link-${type}-${targetId}`}>
                <Copy className="w-4 h-4 mr-2" /> Copy Link
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
